import type { CollabCommentData } from '@examdojo/core/tiptap-react-ui-components/lib/collab-config';
import { hoverOffThread, hoverThread } from '@tiptap-pro/extension-comments';
import type { TCollabComment, TCollabThread, TiptapCollabProvider } from '@tiptap-pro/provider';
import type { Editor } from '@tiptap/core';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback } from 'react';
import { CommentCard } from './comment-card';
import { ThreadComposer } from './thread-composer';

interface ThreadItemProps {
  thread: TCollabThread;
  editor: Editor;
  provider: TiptapCollabProvider;
  userId: string;
  userName: string;
  userAvatar?: string;
  isSelected: boolean;
  isOrphaned: boolean;
  onSelect: (threadId: string) => void;
}

function isCommentOwner(comment: TCollabComment<CollabCommentData>, currentUserId: string): boolean {
  return comment.data?.userId === currentUserId;
}

export function ThreadItem({ thread, editor, provider, userId, userName, userAvatar, isSelected, isOrphaned, onSelect }: ThreadItemProps) {

  const firstComment = thread.comments[0];
  const replies = thread.comments.slice(1);
  const isResolved = !!thread.resolvedAt;

  const handleClick = useCallback(
    (e: ReactMouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('textarea, button, input, [contenteditable]')) return;

      onSelect(thread.id);
    },
    [thread.id, onSelect],
  );

  const handleMouseEnter = useCallback(() => {
    hoverThread(editor, [thread.id]);
  }, [editor, thread.id]);

  const handleMouseLeave = useCallback(() => {
    hoverOffThread(editor);
  }, [editor]);

  const handleResolve = useCallback(() => {
    if (isResolved) {
      editor.commands.unresolveThread({ id: thread.id });
    } else {
      editor.commands.resolveThread({ id: thread.id });
    }
  }, [editor, thread.id, isResolved]);

  const handleDeleteThread = useCallback(() => {
    provider.deleteThread(thread.id);
    editor.commands.removeThread({ id: thread.id });
  }, [editor, provider, thread.id]);

  const handleUpdateComment = useCallback(
    (_threadId: string, commentId: string, content: string) => {
      editor.commands.updateComment({
        threadId: thread.id,
        id: commentId,
        content,
      });
    },
    [editor, thread.id],
  );

  const handleDeleteComment = useCallback(
    (_threadId: string, commentId: string) => {
      editor.commands.removeComment({
        threadId: thread.id,
        id: commentId,
      });
    },
    [editor, thread.id],
  );

  if (!firstComment) return null;

  const isThreadOwner = isCommentOwner(firstComment, userId);

  return (
    <div
      className={`threads-panel-thread ${isSelected ? 'selected' : ''} ${isResolved ? 'resolved' : ''} ${isOrphaned ? 'orphaned' : ''}`}
      onClick={handleClick}
      onMouseEnter={isOrphaned ? undefined : handleMouseEnter}
      onMouseLeave={isOrphaned ? undefined : handleMouseLeave}
    >
      {isOrphaned && <span className="threads-panel-orphaned-label">Deleted</span>}

      <div className="threads-panel-conversation">
        <CommentCard
          comment={firstComment}
          threadId={thread.id}
          onUpdate={isThreadOwner ? handleUpdateComment : undefined}
          onDelete={isThreadOwner ? handleDeleteThread : undefined}
          onResolve={handleResolve}
          isResolved={isResolved}
        />

        {isSelected &&
          replies.map((comment) => {
            const isOwner = isCommentOwner(comment, userId);
            return (
              <CommentCard
                key={comment.id}
                comment={comment}
                threadId={thread.id}
                onUpdate={isOwner ? handleUpdateComment : undefined}
                onDelete={isOwner ? handleDeleteComment : undefined}
              />
            );
          })}

        {isSelected && <ThreadComposer provider={provider} threadId={thread.id} userId={userId} userName={userName} userAvatar={userAvatar} />}
      </div>

      {/* Reply count (collapsed) */}
      {!isSelected && replies.length > 0 && (
        <div className="threads-panel-reply-count">
          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
        </div>
      )}
    </div>
  );
}
